import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";

const baseUrl = config.baseURL;

const ComplaintRequest = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [replies, setReplies] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const [showReplyInput, setShowReplyInput] = useState({}); // Keeps track of reply input visibility for each complaint

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [showResolved, setShowResolved] = useState(true); // Checkbox to show/hide resolved complaints
  const [unresolvedCount, setUnresolvedCount] = useState(0); // Track number of unresolved complaints for the selected company

  const token = localStorage.getItem("token");

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/companies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const companiesData = response.data.data;

        // Fetch unresolved complaints count for each company
        await Promise.all(
          companiesData.map(async (company) => {
            const complaintsResponse = await axios.get(
              `${baseUrl}/api/complain?companyId=${company.id}&resolved=false`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            company.unresolvedCount = complaintsResponse.data.data.length;
            return company;
          })
        );
        setCompanies(companiesData);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, [token]);

  // Fetch complaints by company ID
  useEffect(() => {
    if (selectedCompany) {
      fetchComplaintsByCompany(selectedCompany);
    } else {
      // Reset unresolved count when no company is selected
      setUnresolvedCount(
        companies.reduce((acc, company) => acc + company.unresolvedCount, 0)
      );
    }
  }, [
    selectedCompany,
    page,
    itemsPerPage,
    sortField,
    sortOrder,
    showResolved,
    companies,
  ]);

  const fetchComplaintsByCompany = async (companyId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/complain?companyId=${companyId}&page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const filteredComplaints = response.data.data.filter(
        (complaint) => showResolved || !complaint.resolved
      );
      setComplaints(filteredComplaints);

      // Set unresolved count for the selected company
      const unresolvedComplaints = response.data.data.filter(
        (complaint) => !complaint.resolved
      );
      setUnresolvedCount(unresolvedComplaints.length);
    } catch (error) {
      console.error("Error fetching complaints:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveComplaint = async (complaintId) => {
    try {
      await axios.put(
        `${baseUrl}/api/complain/resolve/${complaintId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Update the complaint to resolved
      setComplaints(
        complaints.map((complaint) =>
          complaint.id === complaintId
            ? { ...complaint, resolved: true }
            : complaint
        )
      );

      // Update unresolved count and notifications
      if (selectedCompany) {
        fetchComplaintsByCompany(selectedCompany); // Re-fetch complaints for the selected company
      } else {
        // If no company is selected, update the unresolved count for all companies
        setUnresolvedCount(
          companies.reduce((acc, company) => acc + company.unresolvedCount, 0)
        );
      }
    } catch (error) {
      console.error("Error resolving complaint:", error);
    }
  };

  const handleSubmitReply = async (complaintId) => {
    const replyText = replies[complaintId];
    if (!replyText) {
      alert("Please enter a reply!");
      return;
    }
    try {
      await axios.post(
        `${baseUrl}/api/complain/reply/${complaintId}`,
        { reply: replyText },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // After submitting the reply, reset the input and refetch complaints
      setReplies({ ...replies, [complaintId]: "" });
      fetchComplaintsByCompany(selectedCompany); // Re-fetch complaints after submitting a reply
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };
  const toggleRepliesVisibility = (complaintId) => {
    setShowReplies((prevState) => ({
      ...prevState,
      [complaintId]: !prevState[complaintId], // Toggle the visibility
    }));
  };
  const toggleReplyInput = (complaintId) => {
    setShowReplyInput((prevState) => ({
      ...prevState,
      [complaintId]: !prevState[complaintId], // Toggle reply input visibility
    }));
  };

  return (
    <div className="dashboard">
      <div className="navbar" style={{ color: "#2E4053" }}>
        <h1>Complaints Management</h1>
      </div>

      <div className="sidebar" style={{ color: "#2E4053" }}>
        <select
          onChange={(e) => setSelectedCompany(e.target.value)}
          value={selectedCompany}
          style={{ color: "#2E4053" }}
        >
          <option value="">Select Company</option>
          {companies.length > 0 ? (
            companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
                {/* Show unresolved complaints count in a badge next to the company name */}
                {company.unresolvedCount > 0 && (
                  <span
                    style={{
                      // backgroundColor: "#FF5733",
                      color: "red",
                      borderRadius: "50%",
                      padding: "2px 8px",
                      marginLeft: "8px",
                    }}
                  >
                    --Unresolved {company.unresolvedCount}
                  </span>
                )}
              </option>
            ))
          ) : (
            <option value="">No companies available</option>
          )}
        </select>
      </div>

      {/* Show unresolved complaints count as notification when a company is selected */}
      <div
        style={{
          backgroundColor: "#FF5733",
          color: "#FFFFFF",
          padding: "10px",
          borderRadius: "5px",
          marginBottom: "20px",
          marginTop: "10px",
        }}
      >
        {selectedCompany
          ? `You have ${unresolvedCount} unresolved complaints for this company.`
          : `You have ${unresolvedCount} unresolved complaints across all companies.`}
      </div>

      <div
        className="complaints-table"
        style={{ backgroundColor: "#F4F6F9", color: "#333333" }}
      >
        <div>
          {/* Checkbox to toggle resolved complaints visibility */}
          <label>
            <input
              type="checkbox"
              checked={showResolved}
              onChange={() => setShowResolved(!showResolved)}
            />
            Show Resolved Complaints
          </label>
        </div>

        {isLoading ? (
          <p>Loading complaints...</p>
        ) : (
          <div>
            {complaints.length > 0 ? (
              <div>
                {complaints.map((complaint, index) => (
                  <div
                    key={complaint.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F8F9FC",
                      padding: "10px",
                      marginBottom: "10px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      border: complaint.resolved
                        ? "2px solid #28a745"
                        : "2px solid #dc3545",
                    }}
                  >
                    <h3>{complaint.module}</h3>
                    <p>{complaint.comment}</p>
                    <p>Created by: {complaint.userName}</p>
                    <p>Company: {complaint.companyName}</p>
                    <p>
                      Created on:{" "}
                      {new Date(complaint.created_at).toLocaleString()}
                    </p>

                    {complaint.resolved ? (
                      <p style={{ color: "#28a745" }}>Status: Resolved</p>
                    ) : (
                      <p style={{ color: "#dc3545" }}>Status: Not Resolved</p>
                    )}

                    {/* Toggle Reply Button */}
                    {!complaint.resolved && (
                      <button
                        onClick={() => toggleReplyInput(complaint.id)} // Toggle visibility of reply input
                        style={{
                          backgroundColor: "#A6AAAD",
                          color: "#2E4053",
                          border: "none",
                          padding: "5px 10px",
                          marginBottom: "10px",
                          margin: "10px",
                        }}
                      >
                        Reply
                      </button>
                    )}

                    {/* Show Reply Input when toggled */}
                    {showReplyInput[complaint.id] && !complaint.resolved && (
                      <>
                        <input
                          type="text"
                          value={replies[complaint.id] || ""}
                          onChange={(e) =>
                            setReplies({
                              ...replies,
                              [complaint.id]: e.target.value,
                            })
                          }
                          placeholder="Write your reply here..."
                          style={{
                            width: "100%",
                            padding: "5px",
                            margin: "5px 0",
                          }}
                        />
                        <button
                          onClick={() => handleSubmitReply(complaint.id)}
                          style={{
                            backgroundColor: "#2E4053",
                            color: "#FFFFFF",
                            border: "none",
                            padding: "5px 10px",
                            margin: "10px",
                          }}
                        >
                          Submit Reply
                        </button>
                      </>
                    )}

                    {/* Show/Hide Replies Button */}
                    {complaint.replies && complaint.replies.length > 0 && (
                      <button
                        onClick={() => toggleRepliesVisibility(complaint.id)}
                        style={{
                          backgroundColor: "#A6AAAD",
                          color: "#2E4053",
                          border: "none",
                          padding: "5px 10px",
                          marginBottom: "10px",
                          margin: "10px",
                        }}
                      >
                        {showReplies[complaint.id]
                          ? "Hide Replies"
                          : "Show Replies"}
                      </button>
                    )}

                    {/* Render Replies if Visible */}
                    {showReplies[complaint.id] &&
                      complaint.replies &&
                      complaint.replies.length > 0 && (
                        <div style={{ paddingLeft: "20px" }}>
                          {complaint.replies.map((reply, idx) => (
                            <p
                              key={idx}
                              style={{ marginBottom: "5px", color: "#555" }}
                            >
                              {reply.reply} <br />
                              <span style={{ fontSize: "12px", color: "#888" }}>
                                - {reply.userName} at{" "}
                                {new Date(reply.created_at).toLocaleString()}
                              </span>
                            </p>
                          ))}
                        </div>
                      )}

                    {!complaint.resolved && (
                      <button
                        onClick={() => handleResolveComplaint(complaint.id)}
                        style={{
                          backgroundColor: "#2E4053",
                          color: "#FFFFFF",
                          border: "none",
                          padding: "5px 10px",
                        }}
                      >
                        Mark as Resolved
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p>No complaints available.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplaintRequest;
